// Thin wrapper around /api/* routes served by the Express backend on Railway.
// `credentials: 'include'` ensures the signed session cookie rides on every
// request (same-origin in production, proxied in Vite dev).
async function call(path, init = {}) {
  const resp = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  const ct = resp.headers.get("content-type") || "";
  const body = ct.includes("application/json") ? await resp.json() : await resp.text();
  if (!resp.ok) {
    const msg = typeof body === "string" ? body : body?.error || resp.statusText;
    const err = new Error(msg || "Request failed");
    err.status = resp.status;
    throw err;
  }
  return body;
}

export const api = {
  async me() {
    return call("/api/me");
  },

  async login(password) {
    return call("/api/login", { method: "POST", body: JSON.stringify({ password }) });
  },

  async logout() {
    return call("/api/logout", { method: "POST" });
  },

  async listSnapshots(period) {
    return call(`/api/snapshots${period ? `?period=${encodeURIComponent(period)}` : ""}`);
  },

  async listPeriods() {
    return call("/api/snapshots?meta=periods");
  },

  async listUploads() {
    return call("/api/snapshots?meta=history");
  },

  // Returns a same-origin URL for the archived .xlsx. Use directly as
  // <a href={...} download> — the browser attaches the session cookie.
  uploadFileUrl(relPath) {
    return `/api/uploads/file?path=${encodeURIComponent(relPath)}`;
  },

  async listProperties({ includeArchived = false } = {}) {
    const qs = includeArchived ? "?include_archived=1" : "";
    return call(`/api/properties${qs}`);
  },

  async saveProperties(items) {
    return call("/api/properties/bulk", {
      method: "PUT",
      body: JSON.stringify({ items }),
    });
  },

  async archiveProperty(code) {
    return call(`/api/properties/${encodeURIComponent(code)}`, { method: "DELETE" });
  },

  async restoreProperty(code) {
    return call(`/api/properties/${encodeURIComponent(code)}/restore`, { method: "POST" });
  },

  async purgeProperty(code) {
    return call(`/api/properties/${encodeURIComponent(code)}/purge`, { method: "DELETE" });
  },

  // `period` is an optional server-side override; when omitted, the server
  // uses each sheet's parsed A4 period ("March 2026 - Accrual..." → "2026-03").
  async uploadSnapshot({ file, notes } = {}) {
    if (!file) throw new Error("file is required");
    const buf = await file.arrayBuffer();
    const b64 = arrayBufferToBase64(buf);
    return call("/api/ingest", {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        file_base64: b64,
        notes: notes || null,
      }),
    });
  },

  async getSettings() {
    return call("/api/settings");
  },

  async saveSettings(body) {
    return call("/api/settings", { method: "PUT", body: JSON.stringify(body) });
  },

  async narrative(payload) {
    return call("/api/narrative", { method: "POST", body: JSON.stringify(payload) });
  },

  async getNarrative(period) {
    return call(`/api/narrative?period=${encodeURIComponent(period)}`);
  },
};

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}
